import { createSlice } from '@reduxjs/toolkit';
import { statusTypes } from '@/constants/types';
import { spinTheWheelMessages } from '@/constants/message';

import {
  fetchSpinningResult,
  fetchWheelData,
} from '../thunk/spin-the-wheel-thunk';

const setToInitialSpinResultDataForCloseModal = {
  isOpenSpinToWinModal: false,
  error: null,

  spinResult: null,
  spinningResultFetchStatus: statusTypes.idle,
  selectedWheelIndex: null,
  spinningStatusTrack: statusTypes.idle,

  isOpenSuccessSpinModal: false,
};

const setToInitialSpinResultDataForLogout = {
  isOpenSpinToWinModal: false,
  error: null,

  spinResult: null,
  spinningResultFetchStatus: statusTypes.idle,
  selectedWheelIndex: null,
  spinningStatusTrack: statusTypes.idle,

  isOpenSuccessSpinModal: false,

  isOpenErrorSpinModal: {
    status: false,
    data: '',
  },

  isCouponCopiedSuccess: false,

  isEnabledFakeSpinner: false,
};

const initialState = {
  wheelList: [],
  baseOffset: 0,
  wheelDetails: null,
  fetchStatus: statusTypes.idle,

  isOpenSpinToWinModal: false,
  error: null,

  spinResult: null,
  spinningResultFetchStatus: statusTypes.idle,
  selectedWheelIndex: null,
  spinningStatusTrack: statusTypes.idle,

  isOpenSuccessSpinModal: false,

  isOpenErrorSpinModal: {
    status: false,
    data: '',
  },

  isCouponCopiedSuccess: false,

  // TODO: used to show fake spinner for guest
  isEnabledFakeSpinner: false,
};

const spinTheWheelSlice = createSlice({
  name: 'spinTheWheel',
  initialState,
  reducers: {
    resetSpinTheWheelForCache() {
      return initialState;
    },
    resetSpinTheWheelForLogout(state) {
      return { ...state, ...setToInitialSpinResultDataForLogout };
    },
    updateSpinningTrack(state, action) {
      return {
        ...state,
        spinningStatusTrack: action.payload,
      };
    },
    updateFakeSpinningTrack(state, action) {
      return {
        ...state,
        isEnabledFakeSpinner: action.payload,
      };
    },
    updateIsCouponCopiedSuccessState(state, action) {
      const { copiedState, isCloseModal } = action.payload;

      return {
        ...state,
        ...(isCloseModal ? setToInitialSpinResultDataForCloseModal : {}),
        isCouponCopiedSuccess: copiedState,
      };
    },
    handleOpenSpinToWinModal(state) {
      return {
        ...state,
        isOpenSpinToWinModal: true,
      };
    },
    updateSpinToWinModalState(state, action) {
      return {
        ...state,
        isOpenSpinToWinModal: action.payload,
      };
    },
    handleOpenSuccessSpinModal(state) {
      return {
        ...state,
        isOpenSuccessSpinModal: true,
      };
    },
    handleCloseSpinToWinModal(state) {
      // Store in sessionStorage when user closes modal
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('spinModalClosed', 'true');
      }

      return {
        ...state,
        ...setToInitialSpinResultDataForCloseModal,
        isOpenErrorSpinModal: {
          status: false,
          data: '',
        },
      };
    },
    handleCloseErrorSpinModal(state) {
      return {
        ...state,
        ...setToInitialSpinResultDataForCloseModal,
        isOpenErrorSpinModal: {
          status: false,
          data: '',
        },
      };
    },

    handleOpenErrorSpinModal: (state, action) => {
      const { data } = action.payload;

      return {
        ...state,
        isOpenErrorSpinModal: {
          ...state.isOpenErrorSpinModal,
          status: true,
          data,
        },
      };
    },

    handleTryAgainSpin(state) {
      return {
        ...state,
        ...setToInitialSpinResultDataForCloseModal,
        isOpenErrorSpinModal: {
          ...state.isOpenErrorSpinModal,
          status: false,
          data: '',
        },
        isOpenSpinToWinModal: true,
      };
    },
    clearSpinTheWheelDataForNewRound(state) {
      return {
        ...state,
        ...setToInitialSpinResultDataForLogout,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWheelData.pending, (state) => {
        state.error = null;
        state.fetchStatus = statusTypes.loading;
      })
      .addCase(fetchWheelData.fulfilled, (state, action) => {
        const responseData = action.payload;

        if (!responseData) {
          state.wheelList = [];
          state.wheelDetails = null;
          state.baseOffset = 0;
        } else {
          const { vouchers, ...rest } = responseData;

          state.wheelList = vouchers?.length > 0 ? [...vouchers] : [];
          state.wheelDetails = { ...rest };

          // base offset - default position 0 degree
          state.baseOffset =
            vouchers?.length > 0 ? 270 - 360 / vouchers.length / 2 : 0;
        }

        state.error = null;
        state.fetchStatus = statusTypes.succeeded;
      })
      .addCase(fetchWheelData.rejected, (state, action) => {
        state.fetchStatus = statusTypes.failed;
        state.error = 'Fetch to failed data';
      })

      // fetch spinning spinResult
      .addCase(fetchSpinningResult.pending, (state) => {
        state.error = null;
        state.spinningResultFetchStatus = statusTypes.spinning;
        state.spinResult = null;
        state.selectedWheelIndex = null;
      })
      .addCase(fetchSpinningResult.fulfilled, (state, action) => {
        const responseData = action.payload;

        if (responseData?.voucher) {
          state.spinResult = { ...responseData.voucher };
          state.spinningResultFetchStatus = statusTypes.succeeded;
          state.selectedWheelIndex = state?.wheelList?.findIndex(
            (item) => item?.name?.en === responseData?.voucher?.name?.en,
          );
          state.error = null;
          state.isOpenErrorSpinModal = {
            status: false,
            data: '',
          };
        }
      })
      .addCase(fetchSpinningResult.rejected, (state, action) => {
        const { status, data } = action.payload;

        if (status === 500) {
          return {
            ...state,
            spinningResultFetchStatus: statusTypes.failed,
            error: 'Something Went Wrong',
            ...setToInitialSpinResultDataForCloseModal,
            isOpenErrorSpinModal: {
              status: true,
              data: spinTheWheelMessages.somethingWentWrong,
            },
          };
        }

        return {
          ...state,
          spinningResultFetchStatus: statusTypes.failed,
          error: data?.error?.message || 'Bad Request',
          spinResult: null,
          selectedWheelIndex: null,
        };
      });
  },
});

export const {
  resetSpinTheWheelForCache,
  handleOpenSpinToWinModal,
  handleCloseSpinToWinModal,
  updateIsOpenErrorSpinModalState,
  handleTryAgainSpin,
  handleCloseErrorSpinModal,
  handleOpenSuccessSpinModal,
  updateSpinningTrack,
  updateIsCouponCopiedSuccessState,
  resetSpinTheWheelForLogout,
  updateSpinToWinModalState,
  updateFakeSpinningTrack,
  clearSpinTheWheelDataForNewRound,
} = spinTheWheelSlice.actions;

export default spinTheWheelSlice.reducer;
